import React, { useEffect } from 'react';
import { Button, Dialog, Divider, FormatCurrency, constants, analyticsHelpers } from 'day8-ui';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FaPlus } from "@react-icons/all-files/fa6/FaPlus";
import { FaMinus } from "@react-icons/all-files/fa6/FaMinus";
import { IoPeopleOutline } from "@react-icons/all-files/io5/IoPeopleOutline";
import { handleCabinSelection } from '../../helpers/bookingType';
import { getAvailableCabinsForWeekAndCategory, getTotalPriceFromSelectedCabins, getSelectedCabins, getCrewFromSelectedCabins, getWaitingListData } from '../../helpers/week';
import { getSelectedFilters } from '../../helpers/filter';
import { CABIN, CABIN_WEEK_STATUS, CREW } from '../../constants';
import { getCabinOptionTitle, getCabinOptionDescription } from '../../helpers/cabinLabels';
import { updateSearch } from '../../helpers/querystring';
import CabinsCrewNotifications from '../CabinsCrewNotifications';
import { useAutomaticDiscounts } from '../../context/AutomaticDiscountsContext';
import { useActiveCompany } from '../../context/ActiveCompanyContext';
import { getDiscountedCabinPrice, getMandatoryExtrasCost, getCabinViewItems } from '../../helpers/pricing';
import { getPromoBookingSubjectFromLocation } from '../../helpers/promoCode';
import UrgencyPill from '../booking/UrgencyPill';
import CrewfinderExtras from '../account/crewfinder/CrewfinderCard/components/CrewfinderExtras';
import { useWaitingList } from '../../context/WaitingListContext';
import { capitalizeFirstLetter } from '../../helpers/utilities';

const { GA4_TRIGGERS } = analyticsHelpers;

const CabinCrewModal = ({ open, onClose, bookingOption, ...props }) => {
  const destinations = useSelector(state => state.destinations.items);
  const cabins = useSelector(state => state.cabins.data);
  const history = useHistory();
  const location = useLocation();
  const { isYachtsAndFriends } = useActiveCompany();
  const { getDiscountForWeekAndSubject } = useAutomaticDiscounts();

  if (!open) {
    return null;
  }

  const packageId = bookingOption?.category?.data?.package?.id ? +bookingOption.category.data.package.id : null;
  const promoSubject = getPromoBookingSubjectFromLocation(location);
  const cabinDiscount = getDiscountForWeekAndSubject(bookingOption?.week?.id, promoSubject, packageId);
  const discountPercentage = cabinDiscount?.discountPercentage || 0;
  const shouldApplyDiscount = !!cabinDiscount && discountPercentage > 0;
  const selectedFilters = getSelectedFilters(location);
  const availableCabins = getAvailableCabinsForWeekAndCategory(cabins, destinations, location, bookingOption.category.data.package.id, bookingOption.week);
  const availableCabinTypes = availableCabins && Object.keys(availableCabins).reduce((acc, cabinType) => {
    Object.keys(availableCabins[cabinType]).map(i => {
      const item = availableCabins[cabinType][i];
      acc.push(item);
    });
    return acc;
  }, []) || [];
  const cabinTypesCount = selectedFilters.cabinType?.length || 0;
  const soloTypesCount = selectedFilters.soloType?.length || 0;
  const totalSelectedGuests = cabinTypesCount * 2 + soloTypesCount;
  const handleCabinTypeSelection = (cabinType, genderType, operator) => {
    const filteredSelectedCabins = selectedFilters[`${cabinType}Type`];
    const index = filteredSelectedCabins && filteredSelectedCabins.indexOf(genderType);
    if (operator === 1) filteredSelectedCabins.push(genderType);
    if (operator === -1) filteredSelectedCabins.splice(index, 1);
    history.replace({
      pathname: location.pathname,
      search: updateSearch(location, {
        [`${cabinType}Type`]: filteredSelectedCabins
      })
    });
  };
  if (availableCabinTypes.length < 1) {
    return null;
  }
  const handleSelection = () => {
    const cabinPathname = isYachtsAndFriends ? '/browse/yachtsandfriends/cabins/confirm' : '/browse/cabins/confirm';
    const openNewTab = false;
    // select_item with the same itemised payload CabinConfirm's view_item uses (now that cabins are chosen)
    const selectedCabins = getSelectedCabins(cabins, destinations, location, bookingOption.week, bookingOption.category.data.package.id);
    const cabinCrew = getCrewFromSelectedCabins(cabins, destinations, location);
    analyticsHelpers.selectItem(
      getCabinViewItems(selectedCabins, bookingOption.week, cabinCrew, bookingOption.category.data.package.name),
      'booking_options',
      'Booking options'
    );
    handleCabinSelection(location, history, cabinPathname, destinations, bookingOption.week, bookingOption.category.data.package, openNewTab);
    onClose();
  };
  // handle availability
  const cabinAvailability = bookingOption.week.available[bookingOption.category.data.package.id].cabin;
  const soloAvailability = bookingOption.week.available[bookingOption.category.data.package.id].solo;
  let packageColor = constants.getPackageColorVariable(bookingOption.category.data.package.id);

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
      scrollable
      title={<h2 className='dui-text-2xl dui-mb-4'>Cabin crew | <span style={{ color: packageColor }}>{bookingOption.category.data.package.name}</span></h2>}
      showFooter
      footerComponent={(
        <div>
          <CabinsCrewNotifications
            availability={bookingOption.week.available[bookingOption.category.data.package.id]}
            maxCapacity={CREW.MAX_GUESTS_ALLOWED_ON_CABIN_BOOKING}
            total={totalSelectedGuests} // total spots selected
            cabinTypesCount={cabinTypesCount}
            soloTypesCount={soloTypesCount}
          />
          {(() => {
            const totalPrice = getTotalPriceFromSelectedCabins(cabins, destinations, location, bookingOption.week, bookingOption.category.data.package.id);
            const selectedPackageData = bookingOption.category.data.package;
            const discountedTotal = shouldApplyDiscount ? getDiscountedCabinPrice(cabins, destinations, location, cabinDiscount, bookingOption.week, selectedPackageData) : null;
            return (
              <p className='dui-font-semibold dui-text-right'>
                Total:{' '}
                {shouldApplyDiscount && discountedTotal !== null ? (
                  <>
                    <FormatCurrency amount={discountedTotal} baseCurrency={bookingOption.week.currency} />
                    <s className='dui-text-gray-400 dui-font-normal dui-text-sm dui-ml-2'><FormatCurrency amount={totalPrice} baseCurrency={bookingOption.week.currency} /></s>
                  </>
                ) : (
                  <FormatCurrency amount={totalPrice} baseCurrency={bookingOption.week.currency} />
                )}
              </p>
            );
          })()}
          <Divider className="dui-mb-4 dui-mt-4" />
          <div className='dui-flex dui-gap-4'>
            <Button className={` dui-w-full`} buttonType="SECONDARY" onClick={onClose} >Cancel</Button>
            <Button className={` dui-w-full`} onClick={handleSelection} disabled={totalSelectedGuests < 1 || totalSelectedGuests > CREW.MAX_GUESTS_ALLOWED_ON_CABIN_BOOKING} >Continue</Button>
          </div>
        </div>
      )}
      {...props}
    >
      <Divider className="dui-mb-4 dui-mt-4" />
      <div className='dui-my-4'>
        <CrewfinderExtras
          hasHost={+bookingOption.category.data.package.id !== +constants.PACKAGES.CLASSIC_MONO}
          hasHalfBoard={+bookingOption.category.data.package.id !== +constants.PACKAGES.CLASSIC_MONO}
          hasAc={bookingOption.category.data.ac}
          hasSkipper={true}
          isViewPage={false}
        />
      </div>
      <Divider className="dui-mb-4 dui-mt-4" />
      <AvailableCabinOptions
        availableCabinTypes={availableCabinTypes}
        selectedFilters={selectedFilters}
        totalSelectedGuests={totalSelectedGuests}
        handleSelection={handleCabinTypeSelection}
        week={bookingOption.week}
        disableCabins={cabinAvailability != null ? cabinTypesCount >= cabinAvailability : false}
        disableSolos={soloAvailability != null ? soloTypesCount >= soloAvailability : false}
        history={history}
        location={location}
        shouldApplyDiscount={shouldApplyDiscount}
        discountPercentage={discountPercentage}
        isOnFullOrder={cabinDiscount?.isOnFullOrder}
        cabinAvailability={cabinAvailability}
        soloAvailability={soloAvailability}
        packageName={bookingOption.category.data.package.name}
      />
    </Dialog>
  );
};

export default CabinCrewModal;

const AvailableCabinOptions = ({ availableCabinTypes = [], selectedFilters = {}, totalSelectedGuests, handleSelection, week, disableCabins, disableSolos, history, location, shouldApplyDiscount, discountPercentage, isOnFullOrder, cabinAvailability, soloAvailability, packageName }) => {
  const waitingListContext = useWaitingList();
  useEffect(() => {
    // clear any selected cabins that are not available (avoids problems when you select any other cabin types ie BOY SOLO on BVI)
    let filteredSelectedCabins = selectedFilters.cabinType.filter(cabinType => {
      return availableCabinTypes.some(item => {
        const isSoloCabin = item.persons === 1 && item.cabinType === CABIN.CABIN_TYPE;
        const cabinTypeToCompare = `${item.type}${isSoloCabin ? 'Solo' : ''}`;
        return item.cabinType === CABIN.CABIN_TYPE && cabinType === cabinTypeToCompare
          && item.cabinWeekStatus !== CABIN_WEEK_STATUS.STATUS_SOLD_OUT;
      });
    });

    let filteredSelectedSolos = selectedFilters.soloType.filter(soloType => { // boy|girl
      return availableCabinTypes.some(item => {
        return item.cabinType === CABIN.SOLO_TYPE && soloType === item.type
          && item.cabinWeekStatus !== CABIN_WEEK_STATUS.STATUS_SOLD_OUT;
      });
    });

    if (cabinAvailability === 0) filteredSelectedCabins = [];
    if (soloAvailability === 0) filteredSelectedSolos = [];

    history.replace({
      pathname: location.pathname,
      search: updateSearch(location, {
        cabinType: filteredSelectedCabins,
        soloType: filteredSelectedSolos
      })
    });
  }, []);
  const isItemUnavailable = item => {
    if (item.cabinWeekStatus === CABIN_WEEK_STATUS.STATUS_SOLD_OUT) return true;
    if (item.cabinType === CABIN.CABIN_TYPE && cabinAvailability === 0) return true;
    if (item.cabinType === CABIN.SOLO_TYPE && soloAvailability === 0) return true;
    return false;
  };
  const sortedCabinTypes = [...availableCabinTypes].sort((a, b) => isItemUnavailable(a) - isItemUnavailable(b));

  return sortedCabinTypes.map((item, i) => {
    const type = item.type || "";
    const cabinType = item.cabinType;
    const isSoloCabin = item.persons === 1 && item.cabinType === CABIN.CABIN_TYPE;
    const genderType = `${type}${isSoloCabin ? 'Solo' : ''}`;
    const count = selectedFilters[`${item.cabinType}Type`] && selectedFilters[`${item.cabinType}Type`].filter(i => i === genderType).length;
    const originalPrice = item.price / (cabinType === CABIN.CABIN_TYPE && !isSoloCabin ? 2 : 1);
    // Calculate discounted price respecting isOnFullOrder
    let discountedPrice = null;
    if (shouldApplyDiscount) {
      if (isOnFullOrder) {
        // Discount applies to full price including extras
        discountedPrice = originalPrice * (1 - discountPercentage / 100);
      } else {
        // Discount only applies to cabin rental portion, not mandatory extras
        const singlePersonCrew = { boys: 1, girls: 0 };
        const mandatoryExtrasPerPerson = getMandatoryExtrasCost({ week }, singlePersonCrew, true);
        const cabinRentalPerPerson = originalPrice - mandatoryExtrasPerPerson;
        const discountedCabinRentalPerPerson = cabinRentalPerPerson * (1 - discountPercentage / 100);
        discountedPrice = discountedCabinRentalPerPerson + mandatoryExtrasPerPerson;
      }
    }
    let plusDisabled = false;
    if (totalSelectedGuests >= CREW.MAX_GUESTS_ALLOWED_ON_CABIN_BOOKING) {
      plusDisabled = true;
    }
    if (cabinType === CABIN.CABIN_TYPE && disableCabins) {
      plusDisabled = true;
    }
    if (cabinType === CABIN.SOLO_TYPE && disableSolos) {
      plusDisabled = true;
    }
    if (item.cabinWeekStatus === CABIN_WEEK_STATUS.STATUS_SOLD_OUT) {
      plusDisabled = true;
    }
    const title = getCabinOptionTitle(item);
    const description = getCabinOptionDescription(item);
    const soldOut = isItemUnavailable(item);
    const itemLabel = `${
      item.cabinType === CABIN.CABIN_TYPE && !isSoloCabin ? '2 ' : ''
    }${capitalizeFirstLetter(type)} ${
      isSoloCabin ? 'solo cabin' : item.cabinType
    }${item.cabinType === CABIN.SOLO_TYPE ? ' spot' : ''}`;
    const waitingListData = getWaitingListData(week?.week, week?.destination?.name, 'cabin', `${packageName} ${itemLabel}`);
    return (
      <div key={i}>
        {i > 0 && (
          <hr
            className="dui-my-0"
            style={{
              backgroundColor: '#d8dde3',
              border: 0,
              height: '1px',
              width: '100%'
            }}
          />
        )}
        <div className="dui-flex dui-justify-between dui-items-center dui-gap-8 dui-py-3">
          <div className='dui-flex dui-items-start dui-min-w-0'>
            <IoPeopleOutline className="dui-flex-shrink-0 dui-mt-1" />
            <div className="dui-flex dui-flex-col dui-gap-0 dui-min-w-0 dui-ml-4">
              <p className="dui-font-bold dui-m-0">{title}</p>
              {description && <p className="dui-text-primary-900 dui-m-0">{description}</p>}
              <div className='dui-font-semibold'>
                {shouldApplyDiscount && discountedPrice !== null ? (
                  <div className="dui-flex dui-items-center dui-gap-2">
                    <div className="dui-m-0"><FormatCurrency amount={discountedPrice} baseCurrency={week?.currency || "EUR"} />/person</div>
                    <s className='dui-text-gray-400 dui-font-normal dui-text-sm'><FormatCurrency amount={originalPrice} baseCurrency={week?.currency || "EUR"} />/person</s>
                  </div>
                ) : (
                  <div className="dui-m-0"><FormatCurrency amount={originalPrice} baseCurrency={week?.currency || "EUR"} />/person</div>
                )}
              </div>
            </div>
          </div>
          <div className='dui-flex dui-flex-col dui-items-center dui-gap-2 dui-flex-shrink-0'>
            {soldOut ? (
              <Button buttonType="SECONDARY" buttonSize="EXTRA_SMALL" onClick={() => waitingListContext.open(waitingListData)}>Join Waitlist</Button>
            ) : (
              <div className="dui-flex dui-items-center dui-gap-4">
                <Button className={`${GA4_TRIGGERS.REMOVE_CABIN_CREW_CLASS}`} buttonType="SECONDARY" buttonSize="EXTRA_SMALL" rightIcon={<FaMinus />} onClick={() => handleSelection(item.cabinType, genderType, -1)} disabled={count === 0} />
                <span>{count}</span>
                <Button className={`${GA4_TRIGGERS.ADD_CABIN_CREW_CLASS}`} buttonType="SECONDARY" buttonSize="EXTRA_SMALL" rightIcon={<FaPlus />} onClick={() => handleSelection(item.cabinType, genderType, +1)} disabled={plusDisabled} />
              </div>
            )}
            {(() => {
              const urgencyLimit = item.cabinWeekStatus === CABIN_WEEK_STATUS.STATUS_SOLD_OUT? 0: item.cabinType === CABIN.SOLO_TYPE? (soloAvailability != null && soloAvailability <= 3 ? soloAvailability : null): (cabinAvailability != null && cabinAvailability <= 3 ? cabinAvailability : null);
              return urgencyLimit != null ? <UrgencyPill className="dui-text-xs md:dui-text-sm" limit={urgencyLimit} /> : null;
            })()}
          </div>
        </div>
      </div>
    );
  });
};
